import { EDIT_KEYS } from '../advanced-editor/constants';

const anyEditsApplied = (photo) => EDIT_KEYS.some((key) => Boolean(photo[key]));

const sortByRootImagesInUploadOrder = (a, b) => {
  const aIsRoot = a.root_image_id === a.image_id ? 0 : 1;
  const bIsRoot = b.root_image_id === b.image_id ? 0 : 1;
  return aIsRoot - bIsRoot || a.image_id - b.image_id;
};

/**
 * Identifies duplicate photos across different print sizes.
 * A photo is considered a duplicate if it has the same MD5 checksum and print options (size, finish)
 * as another photo, and neither has been edited.
 *
 * @param {array} sizes - An array of print size objects, each containing photos to be checked for duplicates.
 */
function listDuplicatePhotos(sizes = []) {
  const allPhotosWithDuplicates = sizes.reduce(
    (originalPhotoList, { key, photos = [] }) => {
      // stable sort photos using preference for root images in order of upload so
      // original images are the earliest versions
      // Use copy to avoid mutating sizes
      const sortedPhotos = [...photos].sort(sortByRootImagesInUploadOrder);

      sortedPhotos.forEach((photo) => {
        // skip zero quantity photos
        if (photo.quantity < 1) {
          return;
        }

        const originalListKey = `${photo.md5}--${key}`;
        const currentPhotoHasEdits = anyEditsApplied(photo);
        const duplicatePhotoWithSameKey = originalPhotoList[originalListKey];

        if (
          // current photo is a duplicate if...
          duplicatePhotoWithSameKey && // other file matched by checksum has same print options
          !currentPhotoHasEdits // this photo hasn't been edited
        ) {
          // this photo is a duplicate of another
          duplicatePhotoWithSameKey.duplicateImageIds.push(photo.image_id);
        } else if (!currentPhotoHasEdits) {
          // unless this photo has had edits then it is unique so store it as an "original"
          originalPhotoList[originalListKey] = {
            key,
            md5: photo.md5,
            imageId: photo.image_id,
            duplicateImageIds: [],
          };
        }
      });

      return originalPhotoList;
    },
    {},
  );

  const photosWithDuplicates = Object.values(allPhotosWithDuplicates).filter(
    (photo) => photo.duplicateImageIds.length,
  );
  const totalDuplicatesCount = photosWithDuplicates.reduce(
    (count, { duplicateImageIds = [] }) => count + duplicateImageIds.length,
    0,
  );

  return { photosWithDuplicates, totalDuplicatesCount };
}

export { listDuplicatePhotos };
